/**
 * InvoiceTemplateSection — where a shop designs what its bills look like.
 *
 * ── Why this screen is shaped the way it is ─────────────────────────────────
 * A till prints TWO different documents: the thermal slip handed over at the
 * counter, and the A4 tax invoice printed or sent as a PDF. They share almost
 * nothing — different paper, different fields, different law about what must
 * appear.
 *
 * The first version of this page put both in one long scroll: paper width,
 * then a preset, then the thermal preview, then the A4 layout and its own
 * preview, then ten toggles, then three text boxes. Nothing said which
 * document a given control belonged to, and the preview for each sat far
 * enough from its controls that you changed a switch, scrolled to look, then
 * scrolled back. It read as a pile of boxes.
 *
 * So: pick the document first, and it is named in plain words rather than by
 * its settings. Then numbered steps, because a settings page with no order
 * reads as a list of unrelated choices. Then the preview beside the controls
 * and pinned, so the effect of a switch is visible at the moment it is
 * flipped. The save bar is pinned for the same reason — this page is longer
 * than a screen and the button used to be off the bottom of it.
 *
 * Settings persisted to the `settings` WatermelonDB table, mirrored into
 * useSettingsStore for synchronous reads by the print pipeline:
 *   THERMAL_PRINTER_PAPER_WIDTH  '58' | '80' | '112'
 *   RECEIPT_CONFIG               JSON of ReceiptConfig (field toggles + texts)
 *   RECEIPT_TEMPLATE             preset name (kept for the digital receipt)
 *   INVOICE_SHOW_LOGO            'true' | 'false'
 *   INVOICE_TERMS                free text (kept for the A4/digital copy)
 *   A4_INVOICE_TEMPLATE          which A4 layout
 *   A4_INVOICE_COLUMNS           JSON array of column ids
 *   DEFAULT_BILL_DOCUMENT        'thermal' | 'a4' — the bill printed after a sale
 */

import { notifySuccess } from '@/store/useToastStore';
import { Switch } from '@/components/ui/Switch';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useMemo, useState, type ReactNode } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Check, FileText, Lock, Plus, Receipt } from 'lucide-react';
import { database, txn } from '@/db';
import { Setting } from '@/db/models/System';
import Store from '@/db/models/Store';
import Merchant from '@/db/models/Merchant';
import { useObservable } from '@/hooks/useObservable';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useAppStore } from '@/store/useAppStore';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { cn } from '@/utils/cn';
import { buildReceipt } from '@/printing/engine/ReceiptBuilder';
import {
  A4_TEMPLATES,
  A4_TEMPLATE_SETTING,
  A4_COLUMNS_SETTING,
  buildA4Html,
  resolveA4TemplateId,
  type A4TemplateId,
} from '@/printing/templates/a4Templates';
import {
  A4_COLUMNS,
  defaultA4Columns,
  parseA4Columns,
  serializeA4Columns,
  type A4ColumnId,
} from '@/printing/templates/a4Columns';
import { loadBankDetails } from '@/printing/bankDetails';
import { BILL_DOCUMENT_SETTING, resolveBillDocument, type BillDocument } from '@/printing/billDocument';
import {
  PRESET_CONFIGS,
  resolveReceiptConfig,
  serializeReceiptConfig,
  type ReceiptConfig,
  type ReceiptPreset,
} from '@/printing/engine/receiptConfig';
import type { ReceiptData } from '@/printing/contracts/ReceiptContracts';
import type { PaperWidth } from '@/printing/contracts/PrintTypes';

const PAPER_OPTIONS: { value: PaperWidth; label: string; hint: string }[] = [
  { value: 58, label: '58 mm', hint: 'Compact / handheld' },
  { value: 80, label: '80 mm', hint: 'Most common counter printer' },
  { value: 112, label: '112 mm', hint: 'Wide POS printer' },
];

const PRESET_OPTIONS: { value: ReceiptPreset; label: string; description: string }[] = [
  { value: 'standard', label: 'Standard', description: 'Itemised bill with a tax breakdown — the balanced default.' },
  { value: 'compact', label: 'Compact', description: 'One line per item — saves paper on a busy counter.' },
  { value: 'detailed', label: 'Detailed', description: 'Formal tax invoice with per-item HSN and a GST summary.' },
];

type BoolField =
  | 'showAddress'
  | 'showGstin'
  | 'showPhone'
  | 'showCustomer'
  | 'showHsn'
  | 'showItemSeparators'
  | 'showTaxBreakup'
  | 'showTerms'
  | 'showPoweredBy';

const FIELD_TOGGLES: { key: BoolField; label: string; description: string }[] = [
  { key: 'showAddress', label: 'Store address', description: 'Print the shop address under the store name.' },
  { key: 'showGstin', label: 'GSTIN', description: 'Hide if you are unregistered or a composition dealer.' },
  { key: 'showPhone', label: 'Phone number', description: 'Show the store contact number in the header.' },
  { key: 'showCustomer', label: 'Customer name', description: 'Print the buyer name (or "Walk-in") on the bill.' },
  { key: 'showHsn', label: 'HSN codes', description: 'Show the HSN/SAC code under each line item.' },
  { key: 'showItemSeparators', label: 'Line between items', description: 'Rule a line between one item and the next. Costs a line of paper each.' },
  { key: 'showTaxBreakup', label: 'Tax breakup', description: 'List CGST/SGST separately instead of hiding tax.' },
  { key: 'showTerms', label: 'Terms & conditions', description: 'Print your terms footer (write the text in step 4).' },
  { key: 'showPoweredBy', label: '"Powered by BigxBook"', description: 'Turn off to remove the credit line.' },
];

const TOGGLE_BY_KEY = Object.fromEntries(FIELD_TOGGLES.map((f) => [f.key, f])) as Record<
  BoolField,
  { key: BoolField; label: string; description: string }
>;

/**
 * The toggles, grouped by WHERE on the bill they act.
 *
 * They used to be one undifferentiated list of ten. A shopkeeper wondering
 * "why is my GSTIN printing" had to read all ten to find the answer; grouped
 * by the part of the paper they affect, they can go straight to the top of the
 * bill and stop reading.
 */
const FIELD_GROUPS: { title: string; keys: BoolField[] }[] = [
  { title: 'At the top', keys: ['showAddress', 'showGstin', 'showPhone', 'showCustomer'] },
  { title: 'For each item', keys: ['showHsn', 'showItemSeparators'] },
  { title: 'At the bottom', keys: ['showTaxBreakup', 'showTerms', 'showPoweredBy'] },
];

/** Which of the two documents the page is designing. */
type DesignDoc = BillDocument;

/** Shared input chrome, so the three text fields cannot drift apart. */
const TEXT_INPUT =
  'w-full rounded-md border border-stroke-hairline/15 bg-bg-canvas px-3 py-2 text-body text-fg-primary placeholder:text-fg-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40';

/**
 * The sample sale behind the live preview.
 *
 * The LINE ITEMS are invented on purpose — a designer should not have to make a
 * real sale to see a layout, and a real one would change under them while they
 * work. The HEADER is not: it used to be a hardcoded "Ajay BIZ Store" on MG
 * Road with someone else's GSTIN, so the shopkeeper was choosing toggles like
 * "Store address" and "GSTIN" against a shop that does not exist. Whether YOUR
 * address fits the paper, or your GSTIN is even set, is the whole question the
 * screen is meant to answer.
 */
const SAMPLE_RECEIPT: ReceiptData = {
  storeName: 'Your Store',
  addressLine: undefined,
  gstin: undefined,
  phone: undefined,
  invoiceNumber: 'INV-000042',
  invoiceDate: '03 Jul 2026, 06:30 PM',
  customerName: 'Ravi Kumar',
  items: [
    { name: 'Aashirvaad Atta 5kg', qty: 1, unit: 'pcs', rate: 275, amount: 275, hsn: '1101' },
    { name: 'Amul Butter 100g', qty: 2, unit: 'pcs', rate: 54, amount: 108, hsn: '0405', discountPct: 5 },
    { name: 'Tata Salt 1kg', qty: 1, unit: 'kg', rate: 28, amount: 28, hsn: '2501' },
  ],
  subtotal: 411,
  discount: 5.4,
  taxes: [
    { label: 'CGST', amount: 10.14 },
    { label: 'SGST', amount: 10.14 },
  ],
  total: 425.88,
  payment: { method: 'CASH', paid: 500, change: 74.12 },
};

export function InvoiceTemplateSection() {
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const { setSettingCache } = useSettingsStore();

  // The shop's own bill — printed after every sale. Saved; the page opens on
  // it. It used to open on the receipt every time, so a shop that had set up
  // A4 came back to find "80 mm roll" highlighted and thought it had not saved.
  const [billDoc, setBillDoc] = useState<BillDocument>(() =>
    resolveBillDocument(useSettingsStore.getState().getSetting(BILL_DOCUMENT_SETTING)),
  );
  // Which document is being DESIGNED — a view, not a setting.
  const [doc, setDoc] = useState<DesignDoc>(billDoc);

  const [paperWidth, setPaperWidth] = useState<PaperWidth>(() => {
    const raw = useSettingsStore.getState().getNumber('THERMAL_PRINTER_PAPER_WIDTH');
    return raw === 58 || raw === 112 ? raw : 80;
  });
  const [config, setConfig] = useState<ReceiptConfig>(() =>
    resolveReceiptConfig(useSettingsStore.getState()),
  );
  const [showLogo, setShowLogo] = useState(
    () => useSettingsStore.getState().getSetting('INVOICE_SHOW_LOGO') === 'true',
  );
  const [a4Columns, setA4Columns] = useState<A4ColumnId[]>(() =>
    parseA4Columns(useSettingsStore.getState().getSetting(A4_COLUMNS_SETTING)),
  );
  const [a4Template, setA4Template] = useState<A4TemplateId>(() =>
    resolveA4TemplateId(useSettingsStore.getState().getSetting(A4_TEMPLATE_SETTING)),
  );

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Unsaved changes are said out loud: leaving this page drops them.
  const snapshot = JSON.stringify({ billDoc, paperWidth, config, showLogo, a4Columns, a4Template });
  const [savedSnapshot, setSavedSnapshot] = useState(snapshot);
  const dirty = snapshot !== savedSnapshot;
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));

  // ── The preview's header comes from the real store ──────────────────────
  //
  // Same observables and the same GSTIN fallback as ReceiptPreview, so what is
  // designed here is what prints. A designer previewing someone else's shop
  // cannot answer the questions this screen exists to ask: does MY address fit
  // 58mm paper, is MY GSTIN actually set, is MY shop name too long for one line.
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const stores = useObservable<Store[]>(
    () =>
      activeStoreId
        ? database.get<Store>('stores').query(Q.where('id', activeStoreId)).observe()
        : null,
    [activeStoreId],
    [],
  );
  const store = stores[0];

  const merchants = useObservable<Merchant[]>(
    () =>
      store?.merchantId
        ? database.get<Merchant>('merchants').query(Q.where('id', store.merchantId)).observe()
        : null,
    [store?.merchantId],
    [],
  );
  const merchant = merchants[0];

  const sampleReceipt = useMemo<ReceiptData>(() => {
    if (!store) return SAMPLE_RECEIPT;
    // A store-level override wins over the merchant's GSTIN — the same
    // precedence loadStoreGstIdentity and the print pipeline apply.
    const gstin = store.gstinOverride || merchant?.gstin || undefined;
    const addressLine =
      [store.address, store.city, store.state, store.pincode].filter(Boolean).join(', ') || undefined;
    return {
      ...SAMPLE_RECEIPT,
      storeName: store.name || SAMPLE_RECEIPT.storeName,
      addressLine,
      gstin,
      phone: store.phone || undefined,
    };
  }, [store, merchant?.gstin]);

  const previewText = useMemo(
    () => buildReceipt(sampleReceipt, { paperWidth, autoCut: false }, config).text,
    [sampleReceipt, paperWidth, config],
  );

  // The A4 preview renders the real template into an iframe, so what is shown
  // is the document itself rather than a drawing of it. Bank details come from
  // settings exactly as they will at print time — a shop that has not entered
  // them sees the block missing here too, which is the honest preview.
  const a4PreviewHtml = useMemo(
    () =>
      buildA4Html({ ...sampleReceipt, bank: loadBankDetails(useSettingsStore.getState()) }, a4Template, {
        printedAt: new Date(),
        columns: a4Columns,
      }),
    [sampleReceipt, a4Template, a4Columns],
  );

  const update = <K extends keyof ReceiptConfig>(key: K, value: ReceiptConfig[K]) =>
    setConfig((c) => ({ ...c, [key]: value }));

  /** Choosing a preset seeds all fields from that preset's defaults. */
  const applyPreset = (preset: ReceiptPreset) => setConfig({ ...PRESET_CONFIGS[preset] });

  async function save() {
    if (!merchantId) return;
    setSaving(true);
    setError(null);
    try {
      await txn(async (db) => {
        const upsert = async (key: string, val: string) => {
          const existing = await database
            .get<Setting>('settings')
            .query(Q.where('merchant_id', merchantId), Q.where('setting_key', key))
            .fetch();
          if (existing.length > 0) {
            await existing[0].update((s) => { s.settingValue = val; s.isSynced = false; });
          } else {
            await db.get<Setting>('settings').create((s) => {
              s.merchantId = merchantId;
              s.storeId = null;
              s.category = 'invoice';
              s.settingKey = key;
              s.settingValue = val;
              s.version = 1;
              s.isSynced = false;
              s.isDeleted = false;
            });
          }
          setSettingCache(key, val);
        };
        await upsert('THERMAL_PRINTER_PAPER_WIDTH', String(paperWidth));
        await upsert('RECEIPT_CONFIG', serializeReceiptConfig(config));
        await upsert('RECEIPT_TEMPLATE', config.preset);
        await upsert('INVOICE_SHOW_LOGO', showLogo ? 'true' : 'false');
        await upsert('INVOICE_TERMS', config.terms);
        await upsert(A4_TEMPLATE_SETTING, a4Template);
        await upsert(A4_COLUMNS_SETTING, serializeA4Columns(a4Columns));
        await upsert(BILL_DOCUMENT_SETTING, billDoc);
      });
      setSavedSnapshot(snapshot);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      notifySuccess('Bill design saved.');
    } catch (err) {
      setError((err as Error).message ?? 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  const thermal = doc === 'thermal';

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-heading font-semibold text-fg-primary">Bill design</h2>
        <p className="mt-1 text-body text-fg-secondary">
          A till prints two different documents and they are designed separately. Pick one — the
          preview beside it updates as you go.
        </p>
      </div>

      {/* Which document. The biggest source of confusion here was that thermal
          and A4 controls were interleaved in one scroll, so it was never clear
          which paper a given switch affected. */}
      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 sm:flex-1">
          <div className="text-body font-medium text-fg-primary">Your bill</div>
          <div className="text-label text-fg-secondary">
            Printed after each sale, and listed first on the print buttons. The other stays one tap away.
          </div>
        </div>
        <div role="radiogroup" aria-label="Your bill" className="flex shrink-0 rounded-lg border border-stroke-hairline/20 p-0.5">
          {([['thermal', 'Counter receipt'], ['a4', 'A4 tax invoice']] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={billDoc === value}
              onClick={() => { setBillDoc(value); setDoc(value); }}
              className={cn(
                'flex-1 whitespace-nowrap rounded-md px-3 py-1.5 text-body transition-colors duration-120 ease-apple sm:flex-none',
                billDoc === value ? 'bg-[rgb(var(--accent-solid))] text-white' : 'text-fg-secondary hover:text-fg-primary',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2">
        <DocCard
          active={thermal}
          onClick={() => setDoc('thermal')}
          icon={<Receipt size={18} />}
          title="Counter receipt"
          badge={`${paperWidth} mm roll`}
          blurb="The slip you hand the customer, printed at the counter."
          isBill={billDoc === 'thermal'}
        />
        <DocCard
          active={!thermal}
          onClick={() => setDoc('a4')}
          icon={<FileText size={18} />}
          title="A4 tax invoice"
          badge={A4_TEMPLATES.find((t) => t.id === a4Template)?.label ?? 'Full page'}
          blurb="The full page you print on a laser or inkjet printer, or save as a PDF."
          isBill={billDoc === 'a4'}
        />
      </div>

      {/* Side by side only once there is genuinely room for it. The settings
          shell already spends ~460px on two navigation columns, so splitting at
          the usual large breakpoint left the controls about 240px wide and every
          choice card wrapped to one word per line. Below this, the preview simply
          follows the controls. */}
      <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] 2xl:items-start">
        {/* ── Controls ──────────────────────────────────────────────── */}
        <div className="min-w-0 space-y-4">
          {thermal ? (
            <>
              <Step n={1} title="Paper width" hint="Match the roll loaded in your printer.">
                <div className="grid grid-cols-3 gap-2">
                  {PAPER_OPTIONS.map((opt) => (
                    <ChoiceCard
                      key={opt.value}
                      active={paperWidth === opt.value}
                      onClick={() => setPaperWidth(opt.value)}
                      title={opt.label}
                      blurb={opt.hint}
                    />
                  ))}
                </div>
              </Step>

              <Step
                n={2}
                title="Start from a design"
                hint="A starting point you then adjust. Picking one resets the switches in step 3."
              >
                <div className="grid gap-2 sm:grid-cols-3">
                  {PRESET_OPTIONS.map((opt) => (
                    <ChoiceCard
                      key={opt.value}
                      active={config.preset === opt.value}
                      onClick={() => applyPreset(opt.value)}
                      title={opt.label}
                      blurb={opt.description}
                    />
                  ))}
                </div>
              </Step>

              <Step n={3} title="What to print" hint="Turn off anything your shop does not need.">
                <div className="space-y-4">
                  <FieldGroup title="At the top">
                    <ToggleRow
                      label="Business logo"
                      description="Print your logo above the store name. Upload it under Settings → Business."
                      checked={showLogo}
                      onChange={setShowLogo}
                    />
                    {FIELD_GROUPS[0].keys.map((k) => (
                      <ToggleRow
                        key={k}
                        label={TOGGLE_BY_KEY[k].label}
                        description={TOGGLE_BY_KEY[k].description}
                        checked={config[k]}
                        onChange={(v) => update(k, v)}
                      />
                    ))}
                  </FieldGroup>

                  {FIELD_GROUPS.slice(1).map((group) => (
                    <FieldGroup key={group.title} title={group.title}>
                      {group.keys.map((k) => (
                        <ToggleRow
                          key={k}
                          label={TOGGLE_BY_KEY[k].label}
                          description={TOGGLE_BY_KEY[k].description}
                          checked={config[k]}
                          onChange={(v) => update(k, v)}
                        />
                      ))}
                    </FieldGroup>
                  ))}
                </div>
              </Step>

              <Step n={4} title="Your own words" hint="All optional. Leave blank to print nothing.">
                <div className="space-y-3">
                  <Labelled
                    label="Line under the store name"
                    hint="A tagline, a second phone number — whatever you want there."
                  >
                    <input
                      type="text"
                      value={config.headerTagline}
                      onChange={(e) => update('headerTagline', e.target.value)}
                      maxLength={48}
                      className={TEXT_INPUT}
                      placeholder="Fresh groceries every day"
                    />
                  </Labelled>

                  <Labelled label="Thank-you line at the bottom">
                    <input
                      type="text"
                      value={config.footerMessage}
                      onChange={(e) => update('footerMessage', e.target.value)}
                      maxLength={64}
                      className={TEXT_INPUT}
                      placeholder="Thank you! Visit again."
                    />
                  </Labelled>

                  {config.showTerms ? (
                    <Labelled
                      label="Terms &amp; conditions"
                      hint="One term per line — printed as a numbered list."
                    >
                      <textarea
                        value={config.terms}
                        onChange={(e) => update('terms', e.target.value)}
                        rows={4}
                        className={cn(TEXT_INPUT, 'resize-y')}
                        placeholder="Goods once sold are not returnable."
                      />
                    </Labelled>
                  ) : (
                    <p className="text-label text-fg-tertiary">
                      Turn on &ldquo;Terms &amp; conditions&rdquo; in step 3 to write your terms.
                    </p>
                  )}
                </div>
              </Step>
            </>
          ) : (
            <>
              <Step n={1} title="Layout" hint="Two ways of setting out the same tax invoice.">
                <div className="grid gap-2 sm:grid-cols-2">
                  {A4_TEMPLATES.map((t) => (
                    <ChoiceCard
                      key={t.id}
                      active={a4Template === t.id}
                      onClick={() => setA4Template(t.id)}
                      title={t.label}
                      blurb={t.description}
                    />
                  ))}
                </div>
              </Step>

              {a4Template === 'classic' ? (
                <Step
                  n={2}
                  title="Columns"
                  hint="Four are required on a tax invoice and always print. The rest are yours."
                >
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {A4_COLUMNS.map((c) => {
                        const on = a4Columns.includes(c.id);
                        const label = c.label.replace(/<br\/>/g, ' ');
                        if (c.mandatory) {
                          return (
                            <span
                              key={c.id}
                              title={`${c.hint} Required on a tax invoice.`}
                              className="inline-flex items-center gap-1 rounded-md border border-stroke-hairline/20 bg-bg-subtle px-2.5 py-1.5 text-label text-fg-secondary"
                            >
                              <Lock size={11} className="text-fg-tertiary" />
                              {label}
                            </span>
                          );
                        }
                        return (
                          <button
                            key={c.id}
                            type="button"
                            title={c.hint}
                            aria-pressed={on}
                            onClick={() =>
                              setA4Columns((prev) =>
                                prev.includes(c.id)
                                  ? prev.filter((id) => id !== c.id)
                                  : [...prev, c.id],
                              )
                            }
                            className={cn(
                              'inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-label transition-colors duration-120 ease-apple',
                              on
                                ? 'border-accent bg-accent/5 text-accent-ink'
                                : 'border-stroke-hairline/15 text-fg-secondary hover:border-stroke-hairline/30 hover:text-fg-primary',
                            )}
                          >
                            {on ? <Check size={11} /> : <Plus size={11} />}
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-label text-fg-tertiary">
                      Turning a column off gives its width back to the description.
                    </p>
                    <button
                      type="button"
                      onClick={() => setA4Columns(defaultA4Columns())}
                      className="text-label text-fg-tertiary underline-offset-2 hover:text-fg-secondary hover:underline"
                    >
                      Reset to the standard columns
                    </button>
                  </div>
                </Step>
              ) : (
                <Card className="p-4">
                  <p className="text-label text-fg-tertiary">
                    This layout has a fixed grid of its own. Choose the ruled layout if you want to
                    pick your own columns.
                  </p>
                </Card>
              )}

              <Card className="p-4">
                <p className="text-label text-fg-tertiary">
                  Your logo, address, GSTIN and bank details are not set here — the A4 invoice
                  prints whatever is in Account and Business settings.
                </p>
              </Card>
            </>
          )}
        </div>

        {/* ── Preview, pinned beside the controls ───────────────────── */}
        <div className="2xl:sticky 2xl:top-4">
          <Card className="space-y-2 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-body font-medium text-fg-primary">Preview</h3>
              <span className="text-label uppercase text-fg-tertiary">
                {thermal ? `${paperWidth} mm` : 'A4'} · sample
              </span>
            </div>
            {thermal ? (
              <>
                {showLogo && (
                  <p className="text-label text-fg-tertiary">
                    Your logo prints centred at the very top (not shown in this text preview).
                  </p>
                )}
                <div className="max-h-[58vh] overflow-auto rounded-md bg-bg-subtle p-3">
                  <pre className="mx-auto w-fit whitespace-pre rounded-sm bg-white px-3 py-4 font-mono text-[11px] leading-[1.45] text-neutral-800 shadow-e1">
                    {previewText}
                  </pre>
                </div>
              </>
            ) : (
              <div className="rounded-md bg-bg-subtle p-3">
                <iframe
                  title="A4 invoice preview"
                  srcDoc={a4PreviewHtml}
                  sandbox=""
                  className="block h-[58vh] w-full rounded-sm border border-stroke-hairline/10 bg-white shadow-e1"
                />
              </div>
            )}
            <p className="text-label text-fg-tertiary">
              A made-up sale, in your shop&rsquo;s own details. Nothing here is a real bill.
            </p>
          </Card>
        </div>
      </div>

      {/* Follows you down the page, because this page is longer than a screen
          and the button used to sit off the bottom of it.
          A raised bar rather than a full-width one pinned to the edge: a sticky
          element cannot leave its containing block, which ends 32px above the
          bottom of the scrolling panel, so an edge-to-edge bar always left a
          strip of rows scrolling underneath it. Floating it, with an opaque
          surface and a shadow, makes the layering deliberate instead. */}
      <div className="sticky bottom-3 z-10 flex flex-wrap items-center gap-3 rounded-xl border border-stroke-hairline/20 bg-bg-surface px-4 py-3 shadow-e2">
        <Button onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        {dirty && <span className="text-body text-warning-ink">Unsaved changes</span>}
        <span className="ml-auto text-label text-fg-tertiary">
          Saves both the receipt and the invoice.
        </span>
      </div>
    </div>
  );
}

/** One of the two documents this page designs. */
function DocCard({
  active,
  onClick,
  icon,
  title,
  badge,
  blurb,
  isBill,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  title: string;
  badge: string;
  blurb: string;
  /** This is the shop's bill (see "Your bill"). */
  isBill: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-lg border-2 p-4 text-left transition-colors duration-120 ease-apple',
        active
          ? 'border-accent bg-accent/5'
          : 'border-stroke-hairline/15 hover:border-stroke-hairline/40',
      )}
    >
      <div className="flex items-center gap-2">
        <span className={active ? 'text-accent-ink' : 'text-fg-tertiary'}>{icon}</span>
        <span className="text-body font-medium text-fg-primary">{title}</span>
        <span className="ml-auto shrink-0 text-label uppercase text-fg-tertiary">{badge}</span>
      </div>
      <p className="mt-1.5 text-label text-fg-secondary">{blurb}</p>
      <p className="mt-1.5 text-label">
        {isBill
          ? <span className="font-medium text-accent-ink">Your bill · printed after each sale</span>
          : <span className="text-fg-tertiary">{active ? 'Editing its design' : 'Tap to edit its design'}</span>}
      </p>
    </button>
  );
}

/**
 * A numbered step.
 *
 * A settings page with no stated order reads as a pile of unrelated boxes, and
 * a new shopkeeper does not know whether they are finished. Numbering says
 * there is an order and that following it is enough.
 */
function Step({
  n,
  title,
  hint,
  children,
}: {
  n: number;
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-baseline gap-2.5">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/10 text-label font-medium text-accent-ink">
          {n}
        </span>
        <div className="min-w-0">
          <h3 className="text-body font-medium text-fg-primary">{title}</h3>
          {hint && <p className="mt-0.5 text-label text-fg-tertiary">{hint}</p>}
        </div>
      </div>
      {children}
    </Card>
  );
}

/** A pickable card — paper width, preset, A4 layout. */
function ChoiceCard({
  active,
  onClick,
  title,
  blurb,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  blurb: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-lg border p-3 text-left transition-colors duration-120 ease-apple',
        active
          ? 'border-accent bg-accent/5'
          : 'border-stroke-hairline/15 hover:border-stroke-hairline/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-body font-medium text-fg-primary">{title}</span>
        {active && <Check size={13} className="shrink-0 text-accent-ink" />}
      </div>
      <p className="mt-1 text-label text-fg-secondary">{blurb}</p>
    </button>
  );
}

/** A named run of switches — "At the top", "For each item", "At the bottom". */
function FieldGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-label uppercase text-fg-tertiary">{title}</div>
      <div className="divide-y divide-stroke-hairline/10">{children}</div>
    </div>
  );
}

function Labelled({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="block text-body font-medium text-fg-primary">{label}</span>
      {children}
      {hint && <span className="block text-label text-fg-tertiary">{hint}</span>}
    </label>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <div className="text-body font-medium text-fg-primary">{label}</div>
        <div className="mt-0.5 text-label text-fg-secondary">{description}</div>
      </div>
      <Switch checked={checked} onChange={onChange} label={label} />
    </div>
  );
}
